import { useEffect, useRef, useCallback, useState } from 'react';

interface UseAutoRefreshOptions {
  interval: number; // Intervalo em milliseconds
  enabled: boolean; // Se o auto-refresh está ativo
  onRefresh: () => Promise<void> | void; // Callback para executar o refresh
  minInterval?: number; // Intervalo mínimo para evitar spam
  maxInterval?: number; // Intervalo máximo
  backoffMultiplier?: number; // Multiplicador para backoff em caso de erro
}

interface UseAutoRefreshReturn {
  isRefreshing: boolean;
  lastRefresh: number | null;
  nextRefresh: number | null;
  errorCount: number;
  manualRefresh: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  setInterval: (newInterval: number) => void;
}

export const useAutoRefresh = ({
  interval,
  enabled,
  onRefresh,
  minInterval = 30000,
  maxInterval = 30000,
}: UseAutoRefreshOptions): UseAutoRefreshReturn => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<number | null>(null);
  const [nextRefresh, setNextRefresh] = useState<number | null>(null);
  const [errorCount, setErrorCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Guarda o callback mais recente em uma ref para não recria o timer ao mudar
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  });

  const isRefreshingRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  // executeRefresh é estável — usa ref para o callback
  const executeRefresh = useCallback(async (): Promise<void> => {
    if (isRefreshingRef.current) return;

    try {
      isRefreshingRef.current = true;
      setIsRefreshing(true);
      await onRefreshRef.current();
      setLastRefresh(Date.now());
      setErrorCount(0);
    } catch (error) {
      console.warn('Erro durante auto-refresh:', error);
      setErrorCount(prev => prev + 1);
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, []); // sem dependências — estável durante toda a vida do hook

  const manualRefresh = useCallback(async (): Promise<void> => {
    await executeRefresh();
  }, [executeRefresh]);

  // Timer principal — só recria quando interval/enabled/isPaused mudam
  useEffect(() => {
    if (!enabled || isPaused) {
      setNextRefresh(null);
      return;
    }

    const clampedInterval = Math.max(minInterval, Math.min(maxInterval, interval));
    setNextRefresh(Date.now() + clampedInterval);

    timerRef.current = window.setInterval(() => {
      setNextRefresh(Date.now() + clampedInterval);
      executeRefresh();
    }, clampedInterval);

    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, isPaused, interval, minInterval, maxInterval, executeRefresh]);

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);
  const setIntervalValue = useCallback((_newInterval: number) => {}, []);

  return {
    isRefreshing,
    lastRefresh,
    nextRefresh,
    errorCount,
    manualRefresh,
    pause,
    resume,
    setInterval: setIntervalValue,
  };
};
